/**
 * South Dakota State-Specific Diagnostic Questions
 * CORRECTED VERSION - Verified against actual SD regulatory framework
 * 
 * CRITICAL CORRECTION: Original file cited ARSD 44:73 throughout.
 * ARSD 44:73 = NURSING FACILITIES (nursing homes), NOT Home Health Agencies!
 * 
 * VERIFIED REGULATORY FRAMEWORK FOR SOUTH DAKOTA HOME HEALTH:
 * 
 * Per SD DOH website (https://doh.sd.gov/healthcare-professionals/health-facility-licensure/facility-types/home-health-agencies-hha/):
 * "South Dakota does not require state licensure for certified HHAs."
 * 
 * South Dakota Home Health Agencies are regulated by:
 * - Federal Medicare Conditions of Participation (42 CFR Part 484)
 * - CMS State Operations Manual Appendix B (Home Health Agencies)
 * - Federal certification through CMS (not state licensure)
 * 
 * ARSD 44:73 regulates NURSING FACILITIES (nursing homes) with:
 * - Resident rooms and beds
 * - Dietary services (44:73:07)
 * - Nursing and related care services (44:73:06)
 * - Construction standards (44:73:12)
 * These do NOT apply to home health agencies.
 * 
 * Non-medical home health providers (bathing, housekeeping, meals) are 
 * NOT regulated by SD DOH at all.
 * 
 * Regulatory Body: Centers for Medicare & Medicaid Services (CMS)
 * State Contact: SD DOH Office of Licensure and Certification
 * Consumer Hotline: 1-800-738-2301
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const SOUTH_DAKOTA_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: FEDERAL CERTIFICATION & OPERATIONS
  // ============================================
  {
    id: 'SD-A1',
    itemId: 'A1',
    stateCode: 'SD',
    title: 'Medicare Certification Status',
    description: 'South Dakota does not require state licensure for HHAs. Medicare-certified HHAs must maintain CMS certification.',
    severity: 'high',
    regulationCode: '42 CFR 484.10',
    regulationName: 'Condition of Participation: Patient Rights',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/medicare/health-safety-standards/guidance-for-laws-regulations/home-health-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Is your home health agency Medicare-certified?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - CMS certified' },
          { value: 'no', label: 'No - not Medicare certified' },
        ],
        required: true,
        helpText: 'South Dakota does not require state licensure for certified HHAs. Medicare certification is through CMS.',
      },
      {
        id: 'q2',
        question: 'What is your CMS Certification Number (CCN)?',
        inputType: 'text',
        helpText: 'Your 6-digit CCN assigned by CMS',
        showCondition: { questionId: 'q1', operator: 'equals', value: 'yes' },
      },
      {
        id: 'q3',
        question: 'When was your last CMS survey?',
        inputType: 'date',
        showCondition: { questionId: 'q1', operator: 'equals', value: 'yes' },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your agency maintains CMS Medicare certification. South Dakota does not require additional state licensure for certified HHAs.',
      atRisk: 'Your agency is not Medicare-certified. While SD does not require state licensure, Medicare certification is required to bill Medicare/Medicaid. Contact CGS Administrators at cgsmedicare.com for enrollment.',
      nonCompliant: 'Medicare certification is required for most home health operations.',
      unknown: 'Verify Medicare certification status with CMS.',
    },
    stateNotes: [
      'South Dakota does NOT require state licensure for certified HHAs',
      'Medicare certification is through CMS, not SD DOH',
      'Non-medical home health providers are NOT regulated by SD DOH',
      'Contact SD DOH Consumer Hotline: 1-800-738-2301 for questions/complaints',
    ],
  },

  {
    id: 'SD-A2',
    itemId: 'A2',
    stateCode: 'SD',
    title: 'Administrator Qualifications',
    description: 'Medicare CoPs require qualified administrators',
    severity: 'high',
    regulationCode: '42 CFR 484.105',
    regulationName: 'Condition of Participation: Organization and Administration',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/medicare/health-safety-standards/guidance-for-laws-regulations/home-health-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a qualified administrator per Medicare CoPs?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is administrator qualification documentation on file?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: { questionId: 'q1', operator: 'equals', value: 'yes' },
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Administrator qualifications meet Medicare CoP requirements with documentation on file.',
      atRisk: 'Administrator qualification documentation is missing. Update personnel files.',
      nonCompliant: 'Agency lacks qualified administrator per 42 CFR 484.105.',
      unknown: 'Verify administrator qualifications meet Medicare CoPs.',
    },
    stateNotes: [
      'SD does not require state licensure for certified HHAs - follows federal Medicare CoPs',
      'Administrator qualifications per 42 CFR 484.105(a)(1)',
      'Must organize and supervise all activities in accordance with accepted practices',
      'Documentation of qualifications must be maintained on file',
    ],
  },

  {
    id: 'SD-A3',
    itemId: 'A3',
    stateCode: 'SD',
    title: 'Written Policies and Procedures',
    description: 'Medicare CoPs require comprehensive written policies',
    severity: 'medium',
    regulationCode: '42 CFR 484.50',
    regulationName: 'Condition of Participation: Patient Rights',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/medicare/health-safety-standards/guidance-for-laws-regulations/home-health-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Which Medicare CoP-required policies does your agency have documented?',
        inputType: 'checklist',
        options: [
          { value: 'patient_rights', label: 'Patient rights and responsibilities (§484.50)' },
          { value: 'plan_of_care', label: 'Plan of care requirements (§484.60)' },
          { value: 'quality', label: 'Quality assessment and performance improvement (§484.65)' },
          { value: 'infection', label: 'Infection prevention and control (§484.70)' },
          { value: 'skilled_services', label: 'Skilled professional services (§484.75)' },
          { value: 'home_health_aide', label: 'Home health aide services (§484.80)' },
          { value: 'clinical_records', label: 'Clinical records (§484.110)' },
          { value: 'personnel', label: 'Personnel qualifications (§484.115)' },
          { value: 'emergency', label: 'Emergency preparedness (§484.102)' },
          { value: 'compliance', label: 'Compliance and ethics (§484.105)' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 9, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 6, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 6, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Policy manual meets Medicare CoP requirements.',
      atRisk: 'Policy manual is missing required CoP sections. CMS surveyors will review for completeness.',
      nonCompliant: 'Multiple required policies missing per Medicare CoPs. Develop immediately.',
      unknown: 'Review policies against 42 CFR Part 484 requirements.',
    },
    stateNotes: [
      'SD follows Medicare Conditions of Participation for policy requirements',
      'All policies from 42 CFR Part 484 applicable to certified HHAs',
      'CMS surveys use State Operations Manual Appendix B for guidance',
      'Policies must be reviewed and updated regularly',
    ],
  },

  {
    id: 'SD-A4',
    itemId: 'A4',
    stateCode: 'SD',
    title: 'Emergency Preparedness',
    description: 'Medicare CoPs require comprehensive emergency preparedness per 42 CFR 484.102',
    severity: 'high',
    regulationCode: '42 CFR 484.102',
    regulationName: 'Condition of Participation: Emergency Preparedness',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://asprtracie.hhs.gov/cmsrule',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written emergency preparedness plan per 42 CFR 484.102?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does your emergency plan address SD-specific hazards? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'blizzard', label: 'Blizzard/severe winter weather (critical for SD)' },
          { value: 'tornado', label: 'Tornado/severe storms' },
          { value: 'flooding', label: 'Flooding' },
          { value: 'power_outage', label: 'Extended power outages' },
          { value: 'pandemic', label: 'Pandemic/infectious disease outbreak' },
        ],
        showCondition: { questionId: 'q1', operator: 'equals', value: 'yes' },
      },
      {
        id: 'q3',
        question: 'Is your emergency plan reviewed and updated at least annually?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: { questionId: 'q1', operator: 'equals', value: 'yes' },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'countLessThan', questionId: 'q2', value: 3 },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Emergency preparedness meets 42 CFR 484.102 with SD-specific hazard planning.',
      atRisk: 'Emergency plan may be incomplete or not annually reviewed. Include blizzard/winter weather preparedness for SD.',
      nonCompliant: 'No emergency preparedness plan violates 42 CFR 484.102.',
      unknown: 'Review emergency preparedness requirements per CMS Appendix Z.',
    },
    stateNotes: [
      'SD Healthcare Coalition: https://www.southdakotahcc.org/',
      'CMS Emergency Preparedness: https://asprtracie.hhs.gov/cmsrule',
      'HHA-specific checklist: https://files.asprtracie.hhs.gov/documents/aspr-tracie-cms-ep-rule-home-health-agency-requirements.pdf',
    ],
  },

  // ============================================
  // SECTION B: PERSONNEL & QUALIFICATIONS
  // ============================================
  {
    id: 'SD-B1',
    itemId: 'B1',
    stateCode: 'SD',
    title: 'Personnel Qualifications',
    description: 'Medicare CoPs require qualified personnel per 42 CFR 484.115',
    severity: 'high',
    regulationCode: '42 CFR 484.115',
    regulationName: 'Condition of Participation: Personnel Qualifications',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/medicare/health-safety-standards/guidance-for-laws-regulations/home-health-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Which personnel qualification documents do you maintain for ALL staff?',
        inputType: 'checklist',
        options: [
          { value: 'app', label: 'Employment application' },
          { value: 'credentials', label: 'Professional licenses/certifications' },
          { value: 'background', label: 'Background check results' },
          { value: 'references', label: 'Reference verification' },
          { value: 'tb', label: 'TB screening documentation' },
          { value: 'health', label: 'Health assessment/clearance' },
          { value: 'orientation', label: 'Orientation completion' },
          { value: 'competency', label: 'Competency evaluations' },
          { value: 'training', label: 'Ongoing training records' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 8, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel files contain required documentation per 42 CFR 484.115.',
      atRisk: 'Some personnel files may be missing required documents.',
      nonCompliant: 'Personnel files missing critical documentation. CMS surveyors will cite incomplete files.',
      unknown: 'Audit personnel files against Medicare CoP requirements.',
    },
    stateNotes: [
      'Personnel qualifications per 42 CFR 484.115',
      'SD DOH verifies compliance during CMS certification surveys',
      'SD Consumer Hotline for complaints: 1-800-738-2301',
      'All clinical staff must meet federal qualification standards',
    ],
  },

  {
    id: 'SD-B2',
    itemId: 'B2',
    stateCode: 'SD',
    title: 'Professional License Verification',
    description: 'Verify all professional licenses with SD licensing boards',
    severity: 'high',
    regulationCode: '42 CFR 484.115',
    regulationName: 'Personnel Qualifications - License Verification',
    agencyName: 'South Dakota Board of Nursing',
    agencyAbbr: 'SD BON',
    agencyPhone: '(605) 362-2760',
    agencyUrl: 'https://doh.sd.gov/boards/nursing/',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify professional licenses (RN, LPN, PT, etc.) with SD licensing boards before hiring?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always verified with SD Board of Nursing/applicable board' },
          { value: 'sometimes', label: 'Sometimes verified' },
          { value: 'no', label: 'No verification process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you track license expiration dates and verify renewal?',
        inputType: 'select',
        options: [
          { value: 'system', label: 'Automated tracking system with alerts' },
          { value: 'manual', label: 'Manual tracking (spreadsheet, calendar)' },
          { value: 'none', label: 'No active tracking' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'includes', questionId: 'q2', value: ['system', 'manual'] },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'sometimes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Professional license verification process is in place.',
      atRisk: 'License verification is inconsistent. Implement systematic verification.',
      nonCompliant: 'Employing unlicensed professionals violates federal and state law.',
      unknown: 'Review license verification processes.',
    },
    stateNotes: [
      'SD Board of Nursing: https://doh.sd.gov/boards/nursing/',
      'SD Board of Medical & Osteopathic Examiners',
      'SD Board of Physical Therapy',
    ],
  },

  {
    id: 'SD-B3',
    itemId: 'B3',
    stateCode: 'SD',
    title: 'Home Health Aide Training',
    description: 'Medicare CoPs require specific HHA training per 42 CFR 484.80',
    severity: 'high',
    regulationCode: '42 CFR 484.80',
    regulationName: 'Condition of Participation: Home Health Aide Services',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/medicare/health-safety-standards/guidance-for-laws-regulations/home-health-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do all home health aides meet the 75-hour training requirement per 42 CFR 484.80?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all HHAs have completed 75+ hours' },
          { value: 'most', label: 'Most have, some may be in progress' },
          { value: 'no', label: 'No / not tracking' },
        ],
        required: true,
        helpText: 'Medicare requires 75 hours of training including 16 hours of supervised practical training',
      },
      {
        id: 'q2',
        question: 'Do HHAs complete 12 hours of in-service training annually?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all HHAs complete 12+ hours annually' },
          { value: 'most', label: 'Most complete required hours' },
          { value: 'no', label: 'No structured annual training' },
        ],
      },
      {
        id: 'q3',
        question: 'Are competency evaluations completed for each HHA?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, documented competency evaluations' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'all' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'most' },
      ], result: 'AT_RISK' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'HHA training meets 42 CFR 484.80 requirements.',
      atRisk: 'Some HHAs may be behind on training requirements.',
      nonCompliant: 'HHA training requirements not met. This is a serious CoP violation.',
      unknown: 'Verify HHA training documentation.',
    },
    stateNotes: [
      '75 hours initial training required per 42 CFR 484.80(b)',
      '16 hours supervised practical training per 42 CFR 484.80(b)',
      '12 hours in-service training annually per 42 CFR 484.80(d)',
      'Competency evaluation by RN required per 42 CFR 484.80(c)',
    ],
  },

  // ============================================
  // SECTION C: BACKGROUND SCREENING
  // ============================================
  {
    id: 'SD-C1',
    itemId: 'C1',
    stateCode: 'SD',
    title: 'Criminal Background Check',
    description: 'Background screening requirements for home health staff',
    severity: 'high',
    regulationCode: '22-46 / 42 CFR 484.115',
    regulationName: 'Background Checks',
    agencyName: 'South Dakota Division of Criminal Investigation',
    agencyAbbr: 'SD DCI',
    agencyPhone: '(605) 773-3331',
    agencyUrl: 'https://dci.sd.gov/',
    questions: [
      {
        id: 'q1',
        question: 'For ALL direct care staff, which background checks do you have on file?',
        inputType: 'checklist',
        options: [
          { value: 'state', label: 'South Dakota State criminal background check' },
          { value: 'federal', label: 'FBI national criminal background check' },
          { value: 'abuse_registry', label: 'SD Abuse and Neglect Registry check' },
          { value: 'sex_offender', label: 'Sex Offender Registry check (sor.sd.gov)' },
          { value: 'oig', label: 'OIG Exclusion List verification' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are background checks completed BEFORE employees have any patient contact?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - always before patient contact' },
          { value: 'no', label: 'No - sometimes they start early' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      // NON_COMPLIANT if staff start before clearance (direct violation)
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      // NON_COMPLIANT if missing required checks
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
      // COMPLIANT only if 4+ checks AND never start before clearance
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 4 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      // AT_RISK if 3+ checks but not all required
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Background check documentation is complete for all direct care staff.',
      atRisk: 'Some required background checks may be missing. Ensure all checks are on file.',
      nonCompliant: 'VIOLATION: Staff working before background clearance OR required checks missing. NO patient contact allowed until clearance received.',
      unknown: 'Audit employee files for background check documentation.',
    },
    stateNotes: [
      'SD DCI processes state criminal background checks: (605) 773-3331',
      'FBI fingerprint checks available through SD DCI',
      'SD Sex Offender Registry: sor.sd.gov',
      'SD Abuse and Neglect Registry check through SD DSS',
      'OIG exclusion list verification required for Medicare participation',
    ],
  },

  {
    id: 'SD-C2',
    itemId: 'C2',
    stateCode: 'SD',
    title: 'SD Abuse and Neglect Registry',
    description: 'Verification against the SD Abuse and Neglect Registry',
    severity: 'high',
    regulationCode: '22-46-7',
    regulationName: 'Abuse Registry Verification',
    agencyName: 'South Dakota Department of Social Services',
    agencyAbbr: 'SD DSS',
    agencyPhone: '(605) 773-3227',
    agencyUrl: 'https://dss.sd.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you check the South Dakota Abuse and Neglect Registry for all new hires?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, for all new hires before employment' },
          { value: 'sometimes', label: 'Sometimes, not consistently' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you document the registry check results in personnel files?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'sometimes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Abuse Registry verification is completed and documented for all new hires.',
      atRisk: 'Abuse Registry checks are not consistently performed.',
      nonCompliant: 'Failure to check Abuse Registry. Contact SD DSS at (605) 773-3227.',
      unknown: 'Verify abuse registry check procedures.',
    },
    stateNotes: [
      'SD Abuse and Neglect Registry per SDCL 22-46-7',
      'Contact SD DSS for registry checks: (605) 773-3227',
      'Document all registry check results in personnel files',
      'Listings on registry may prohibit employment',
    ],
  },

  {
    id: 'SD-C3',
    itemId: 'C3',
    stateCode: 'SD',
    title: 'OIG Exclusion List Verification',
    description: 'Federal requirement to verify employees are not excluded from federal programs',
    severity: 'high',
    regulationCode: '42 USC 1320a-7',
    regulationName: 'OIG Exclusion Verification',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://exclusions.oig.hhs.gov/',
    questions: [
      {
        id: 'q1',
        question: 'How often do you check the OIG exclusion list?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Monthly (best practice)' },
          { value: 'quarterly', label: 'Quarterly' },
          { value: 'hire_only', label: 'At hire only' },
          { value: 'no', label: 'No verification' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'monthly', result: 'COMPLIANT' },
      { condition: 'equals', value: 'quarterly', result: 'AT_RISK' },
      { condition: 'includes', value: ['hire_only', 'no'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'OIG exclusion verification conducted monthly per best practices.',
      atRisk: 'OIG checks should be conducted monthly. Billing for excluded employees is fraud.',
      nonCompliant: 'OIG exclusion verification is required. Check at exclusions.oig.hhs.gov.',
      unknown: 'Verify OIG check procedures.',
    },
    stateNotes: [
      'OIG LEIE check at exclusions.oig.hhs.gov',
      'SAM.gov check for federal debarment',
      'Monthly checks recommended by OIG',
      'Civil monetary penalties up to $100,000 per excluded individual employed',
    ],
  },

  // ============================================
  // SECTION D: HEALTH & SAFETY
  // ============================================
  {
    id: 'SD-D1',
    itemId: 'D1',
    stateCode: 'SD',
    title: 'TB Screening',
    description: 'TB screening requirements for direct care staff',
    severity: 'high',
    regulationCode: '42 CFR 484.70',
    regulationName: 'Condition of Participation: Infection Prevention and Control',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/medicare/health-safety-standards/guidance-for-laws-regulations/home-health-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL direct care staff have current TB screening on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff have current TB screening' },
          { value: 'most', label: 'Most do, some may be expired' },
          { value: 'no', label: 'No / Not tracking' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'What TB testing method do you use?',
        inputType: 'select',
        options: [
          { value: 'tst', label: 'Tuberculin Skin Test (TST)' },
          { value: 'igra', label: 'IGRA blood test (QuantiFERON/T-SPOT)' },
          { value: 'both', label: 'Both methods available' },
          { value: 'none', label: 'No standardized method' },
        ],
      },
    ],
    scoringRules: [
      // 1. NON_COMPLIANT - Check for direct violations first
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      // 2. COMPLIANT - All positive conditions met
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'includes', questionId: 'q2', value: ['tst', 'igra', 'both'] },
      ], result: 'COMPLIANT' },
      // 3. AT_RISK - Partial compliance
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'TB screening documentation is current for all direct care staff.',
      atRisk: 'Some TB screenings may be expired. Review employee files.',
      nonCompliant: 'Staff working without valid TB screening. This is a serious violation.',
      unknown: 'Verify TB screening status for all staff.',
    },
    stateNotes: [
      'TB screening per 42 CFR 484.70 infection control requirements',
      'TST or IGRA blood test acceptable per CDC guidelines',
      'Follow SD DOH TB control recommendations',
      'Document all TB screening in personnel files',
    ],
  },

  {
    id: 'SD-D2',
    itemId: 'D2',
    stateCode: 'SD',
    title: 'Infection Prevention and Control',
    description: 'Medicare CoPs require comprehensive infection control per 42 CFR 484.70',
    severity: 'high',
    regulationCode: '42 CFR 484.70',
    regulationName: 'Condition of Participation: Infection Prevention and Control',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/medicare/health-safety-standards/guidance-for-laws-regulations/home-health-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Does your infection prevention and control program include:',
        inputType: 'checklist',
        options: [
          { value: 'policies', label: 'Written infection control policies and procedures' },
          { value: 'training', label: 'Staff training on infection control' },
          { value: 'surveillance', label: 'Infection surveillance and reporting' },
          { value: 'standard', label: 'Standard precautions implementation' },
          { value: 'ppe', label: 'PPE availability and proper use protocols' },
          { value: 'hand_hygiene', label: 'Hand hygiene protocols' },
          { value: 'antibiotic', label: 'Antibiotic stewardship elements' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Infection prevention and control program meets 42 CFR 484.70 requirements.',
      atRisk: 'Infection control program is incomplete.',
      nonCompliant: 'Infection control program does not meet Medicare CoP requirements.',
      unknown: 'Review infection control program against 42 CFR 484.70.',
    },
    stateNotes: [
      'Infection control per 42 CFR 484.70',
      'Standard precautions and hand hygiene protocols required',
      'PPE availability and proper use training',
      'Antibiotic stewardship elements per CMS requirements',
    ],
  },

  // ============================================
  // SECTION E: PATIENT CARE
  // ============================================
  {
    id: 'SD-E1',
    itemId: 'E1',
    stateCode: 'SD',
    title: 'Plan of Care',
    description: 'Medicare CoPs require individualized plans of care per 42 CFR 484.60',
    severity: 'high',
    regulationCode: '42 CFR 484.60',
    regulationName: 'Condition of Participation: Care Planning, Coordination, and Quality of Care',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/medicare/health-safety-standards/guidance-for-laws-regulations/home-health-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written plan of care for EVERY patient before services begin?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before first visit' },
          { value: 'usually', label: 'Usually, but sometimes services start first' },
          { value: 'no', label: 'No formal care plan process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do your plans of care include all required elements per 42 CFR 484.60?',
        inputType: 'checklist',
        options: [
          { value: 'diagnosis', label: 'All pertinent diagnoses' },
          { value: 'mental_status', label: 'Mental status assessment' },
          { value: 'services', label: 'Types of services, supplies, and equipment required' },
          { value: 'frequency', label: 'Frequency and duration of visits' },
          { value: 'prognosis', label: 'Prognosis' },
          { value: 'rehab', label: 'Rehabilitation potential' },
          { value: 'functional', label: 'Functional limitations' },
          { value: 'activities', label: 'Activities permitted' },
          { value: 'safety', label: 'Safety measures' },
          { value: 'nutritional', label: 'Nutritional requirements' },
          { value: 'medications', label: 'Medications and treatments' },
          { value: 'goals', label: 'Patient-specific goals' },
          { value: 'discharge', label: 'Discharge planning' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'countAtLeast', questionId: 'q2', value: 10 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'usually' },
        { condition: 'countAtLeast', questionId: 'q2', value: 6 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Plans of care are completed before services and include all required elements per 42 CFR 484.60.',
      atRisk: 'Plans of care may be incomplete or created after services begin.',
      nonCompliant: 'Services provided without written plans of care violates 42 CFR 484.60.',
      unknown: 'Review plan of care process against Medicare CoPs.',
    },
    stateNotes: [
      'Plan of care requirements per 42 CFR 484.60',
      'Physician-ordered plan required before services begin',
      'All 13 required elements must be included',
      'Review and update at least every 60 days per Medicare CoPs',
    ],
  },

  {
    id: 'SD-E2',
    itemId: 'E2',
    stateCode: 'SD',
    title: 'RN Supervision of Home Health Aides',
    description: 'Medicare CoPs require RN supervision per 42 CFR 484.80',
    severity: 'high',
    regulationCode: '42 CFR 484.80(h)',
    regulationName: 'Supervision of Home Health Aides',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/medicare/health-safety-standards/guidance-for-laws-regulations/home-health-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Are home health aides supervised by an RN per Medicare requirements?',
        inputType: 'select',
        options: [
          { value: 'per_regs', label: 'Yes, RN supervision every 14 days or as required' },
          { value: 'less_frequent', label: 'Yes, but less frequently than required' },
          { value: 'no', label: 'No formal RN supervision' },
        ],
        required: true,
        helpText: 'Per 42 CFR 484.80(h), RN must make supervisory visit every 14 days (can be every 60 days if certain conditions met)',
      },
      {
        id: 'q2',
        question: 'Is supervisory visit documentation maintained in patient records?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'per_regs' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'less_frequent' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'RN supervision of HHAs meets 42 CFR 484.80(h) requirements with documentation.',
      atRisk: 'Supervision may not meet frequency requirements or documentation is incomplete.',
      nonCompliant: 'HHAs not receiving required RN supervision. This is a serious CoP violation.',
      unknown: 'Review RN supervision procedures against Medicare CoPs.',
    },
    stateNotes: [
      'RN supervision every 14 days (or 60 days if conditions met) per 42 CFR 484.80(h)',
      'Supervision must occur while patient is receiving care',
      'Document all supervisory visits in patient records',
      'Written instructions for HHA from RN required',
    ],
  },

  {
    id: 'SD-E3',
    itemId: 'E3',
    stateCode: 'SD',
    title: 'Patient Rights',
    description: 'Medicare CoPs require patient rights notification per 42 CFR 484.50',
    severity: 'high',
    regulationCode: '42 CFR 484.50',
    regulationName: 'Condition of Participation: Patient Rights',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/medicare/health-safety-standards/guidance-for-laws-regulations/home-health-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide ALL patients with written notice of their rights?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all patients at admission' },
          { value: 'most', label: 'Most patients' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you inform patients of the SD DOH Consumer Hotline (1-800-738-2301)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Patient rights notification meets 42 CFR 484.50 requirements.',
      atRisk: 'Some patients may not receive rights notice.',
      nonCompliant: 'Patients not receiving written rights violates 42 CFR 484.50.',
      unknown: 'Review patient rights notification procedures.',
    },
    stateNotes: [
      'SD DOH Consumer Home Health Hotline: 1-800-738-2301',
      'Patients can file complaints with SD DOH',
    ],
  },

  // ============================================
  // SECTION F: CLINICAL RECORDS & QUALITY
  // ============================================
  {
    id: 'SD-F1',
    itemId: 'F1',
    stateCode: 'SD',
    title: 'Clinical Records',
    description: 'Medicare CoPs require comprehensive clinical records per 42 CFR 484.110',
    severity: 'high',
    regulationCode: '42 CFR 484.110',
    regulationName: 'Condition of Participation: Clinical Records',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/medicare/health-safety-standards/guidance-for-laws-regulations/home-health-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do patient clinical records include all required elements per 42 CFR 484.110?',
        inputType: 'checklist',
        options: [
          { value: 'patient_info', label: 'Patient identification and demographics' },
          { value: 'consent', label: 'Consent for services and advance directives' },
          { value: 'physician_orders', label: "Physician's plan of treatment" },
          { value: 'assessments', label: 'Comprehensive assessments (OASIS)' },
          { value: 'care_plan', label: 'Individualized plan of care' },
          { value: 'visit_notes', label: 'Documentation of each visit' },
          { value: 'progress', label: 'Progress notes' },
          { value: 'medications', label: 'Medication profile' },
          { value: 'coordination', label: 'Care coordination documentation' },
          { value: 'discharge', label: 'Discharge or transfer summary' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 9, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 6, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 6, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Clinical records contain all required elements per 42 CFR 484.110.',
      atRisk: 'Clinical records may be missing some required elements.',
      nonCompliant: 'Clinical records do not meet Medicare CoP requirements.',
      unknown: 'Review clinical record documentation against 42 CFR 484.110.',
    },
    stateNotes: [
      'Clinical record requirements per 42 CFR 484.110',
      'All 10 required elements must be in each record',
      'Records must be legible and promptly completed',
      'Accessible only to authorized personnel',
    ],
  },

  {
    id: 'SD-F2',
    itemId: 'F2',
    stateCode: 'SD',
    title: 'OASIS Data Collection',
    description: 'Medicare requires OASIS assessments for all Medicare/Medicaid patients',
    severity: 'high',
    regulationCode: '42 CFR 484.55',
    regulationName: 'Condition of Participation: Comprehensive Assessment',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/Medicare/Quality-Initiatives-Patient-Assessment-Instruments/HomeHealthQualityInits/OASIS-Data-Sets',
    questions: [
      {
        id: 'q1',
        question: 'Do you complete OASIS assessments for all Medicare/Medicaid patients at required timepoints?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all required timepoints (SOC, ROC, Recert, Transfer, Discharge, Death)' },
          { value: 'most', label: 'Most timepoints' },
          { value: 'no', label: 'No / inconsistent' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are OASIS assessments completed by qualified clinicians (RN, PT, SLP, OT)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'OASIS data collection meets Medicare requirements.',
      atRisk: 'OASIS assessments may not be completed at all required timepoints.',
      nonCompliant: 'OASIS compliance is required for Medicare/Medicaid billing.',
      unknown: 'Verify OASIS data collection procedures.',
    },
    stateNotes: [
      'OASIS assessments per 42 CFR 484.55',
      'Required timepoints: SOC, ROC, Recert, Transfer, Discharge, Death',
      'Must be completed by qualified clinician (RN, PT, SLP, OT)',
      'OASIS data transmitted to CMS for quality reporting',
    ],
  },

  {
    id: 'SD-F3',
    itemId: 'F3',
    stateCode: 'SD',
    title: 'Quality Assessment and Performance Improvement',
    description: 'Medicare CoPs require QAPI program per 42 CFR 484.65',
    severity: 'medium',
    regulationCode: '42 CFR 484.65',
    regulationName: 'Condition of Participation: Quality Assessment and Performance Improvement',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/medicare/health-safety-standards/guidance-for-laws-regulations/home-health-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have an ongoing QAPI program that includes:',
        inputType: 'checklist',
        options: [
          { value: 'written', label: 'Written QAPI policies and procedures' },
          { value: 'monitoring', label: 'Ongoing data collection and monitoring' },
          { value: 'outcomes', label: 'Focus on patient outcomes' },
          { value: 'indicators', label: 'Use of outcome indicators (OASIS-based)' },
          { value: 'improvement', label: 'Performance improvement projects' },
          { value: 'documentation', label: 'Documentation of QAPI activities' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'QAPI program meets 42 CFR 484.65 requirements.',
      atRisk: 'QAPI program may be incomplete.',
      nonCompliant: 'QAPI program does not meet Medicare CoP requirements.',
      unknown: 'Review QAPI program against 42 CFR 484.65.',
    },
    stateNotes: [
      'QAPI program per 42 CFR 484.65',
      'Written QAPI plan required',
      'Ongoing data collection and monitoring',
      'Performance improvement projects using OASIS-based indicators',
    ],
  },

  // ============================================
  // SECTION G: ADDITIONAL REQUIREMENTS
  // ============================================
  {
    id: 'SD-G1',
    itemId: 'G1',
    stateCode: 'SD',
    title: 'HIPAA Compliance',
    description: 'Federal HIPAA requirements for patient privacy',
    severity: 'high',
    regulationCode: '45 CFR Parts 160, 164',
    regulationName: 'HIPAA Privacy and Security Rules',
    agencyName: 'U.S. Department of Health and Human Services',
    agencyAbbr: 'HHS OCR',
    agencyPhone: '(800) 368-1019',
    agencyUrl: 'https://www.hhs.gov/hipaa/',
    questions: [
      {
        id: 'q1',
        question: 'Do all staff complete HIPAA privacy and security training?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff trained with documentation' },
          { value: 'most', label: 'Most staff trained' },
          { value: 'no', label: 'No formal HIPAA training' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you have written HIPAA policies and procedures?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'HIPAA compliance documentation is in place.',
      atRisk: 'Some staff may be missing HIPAA training.',
      nonCompliant: 'HIPAA compliance is federally required.',
      unknown: 'Verify HIPAA compliance.',
    },
    stateNotes: [
      'HIPAA Privacy Rule: 45 CFR Part 160 and Subparts A and E of Part 164',
      'HIPAA Security Rule: 45 CFR Part 160 and Subparts A and C of Part 164',
      'All staff must receive HIPAA training and sign confidentiality agreements',
      'HHS OCR enforces HIPAA: (800) 368-1019',
    ],
  },

  {
    id: 'SD-G2',
    itemId: 'G2',
    stateCode: 'SD',
    title: 'Record Retention',
    description: 'Record retention requirements for clinical and personnel records',
    severity: 'medium',
    regulationCode: '42 CFR 484.110(d)',
    regulationName: 'Clinical Record Retention',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/medicare/health-safety-standards/guidance-for-laws-regulations/home-health-agencies',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain clinical records?',
        inputType: 'select',
        options: [
          { value: '5plus', label: '5 years or longer after discharge' },
          { value: '3to4', label: '3-4 years' },
          { value: 'less3', label: 'Less than 3 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: 'Medicare requires retention for 5 years after discharge',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: '5plus', result: 'COMPLIANT' },
      { condition: 'equals', value: '3to4', result: 'AT_RISK' },
      { condition: 'includes', value: ['less3', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Record retention meets Medicare requirements (5 years).',
      atRisk: 'Medicare requires 5-year record retention. Update your policy.',
      nonCompliant: 'Record retention does not meet Medicare requirements.',
      unknown: 'Review record retention requirements.',
    },
    stateNotes: [
      'Medicare requires 5 years after discharge per 42 CFR 484.110(d)',
      'Applies to paper and electronic records',
      'Safeguarded against loss or unauthorized use',
      'Records must be available for CMS surveys',
    ],
  },

  {
    id: 'SD-G3',
    itemId: 'G3',
    stateCode: 'SD',
    title: 'Medicaid Enrollment (SD DSS)',
    description: 'South Dakota Medicaid enrollment for home health services',
    severity: 'medium',
    regulationCode: 'SD DSS Medicaid Manual',
    regulationName: 'Medicaid Provider Enrollment',
    agencyName: 'South Dakota Department of Social Services',
    agencyAbbr: 'SD DSS',
    agencyPhone: '(866) 718-0084',
    agencyUrl: 'https://dss.sd.gov/medicaid/providers/',
    questions: [
      {
        id: 'q1',
        question: 'Is your agency enrolled as a South Dakota Medicaid provider?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes - currently enrolled' },
          { value: 'in_process', label: 'Enrollment in process' },
          { value: 'no', label: 'No - not Medicaid enrolled' },
          { value: 'na', label: 'N/A - do not serve Medicaid patients' },
        ],
        required: true,
        helpText: 'Contact Provider Enrollment at 1-866-718-0084 to enroll',
      },
    ],
    scoringRules: [
      { condition: 'includes', value: ['yes', 'na'], result: 'COMPLIANT' },
      { condition: 'equals', value: 'in_process', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Medicaid enrollment status is appropriate for your agency.',
      atRisk: 'Consider Medicaid enrollment to serve SD Medicaid patients. Contact (866) 718-0084.',
      nonCompliant: 'Medicaid enrollment may be needed.',
      unknown: 'Verify Medicaid enrollment status.',
    },
    stateNotes: [
      'SD Medicaid Provider Enrollment: 1-866-718-0084',
      'SD Medicaid Billing Manual: https://dss.sd.gov/docs/medicaid/providers/billingmanuals/',
    ],
  },
];

export default SOUTH_DAKOTA_QUESTIONS;